import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

interface FieldConfig {
  label: string
  placeholder: string
  description?: string
  initialValue?: string
}

interface ApiKeyCredentialFormProps {
  onSubmit: (data: {
    clientId?: string
    clientSecret: string
    accountId?: string
  }) => Promise<void>
  accountIdConfig?: FieldConfig
  clientIdConfig?: FieldConfig
  clientSecretConfig?: FieldConfig
  confirmationText?: string
  componentId?: string
  submitButtonText?: string
}

export function ApiKeyCredentialForm({
  onSubmit,
  clientIdConfig,
  clientSecretConfig = {
    label: 'API Key',
    placeholder: 'Enter your API key',
  },
  accountIdConfig,
  submitButtonText = 'Connect',
  confirmationText,
  componentId = 'api-key-credential-form',
}: ApiKeyCredentialFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const showClientId = !!clientIdConfig
  const showAccountId = !!accountIdConfig

  const formSchema = z.object({
    clientId: showClientId
      ? z.string().min(1, `${clientIdConfig.label} is required`)
      : z.string().optional(),
    clientSecret: z.string().min(1, `${clientSecretConfig.label} is required`),
    accountId: showAccountId
      ? z.string().min(1, `${accountIdConfig.label} is required`)
      : z.string().optional(),
  })

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientId: clientIdConfig?.initialValue || '',
      clientSecret: clientSecretConfig?.initialValue || '',
      accountId: accountIdConfig?.initialValue || '',
    },
  })

  useEffect(() => {
    form.reset({
      clientId: clientIdConfig?.initialValue || '',
      clientSecret: clientSecretConfig?.initialValue || '',
      accountId: accountIdConfig?.initialValue || '',
    })
  }, [
    clientIdConfig?.initialValue,
    clientSecretConfig?.initialValue,
    accountIdConfig?.initialValue,
    form,
  ])

  const FloatingIslandContent = () => {
    const { hasUnsavedChanges } = useFormChangeTracking()

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId={componentId}
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          {confirmationText && (
            <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
              <span>{confirmationText}</span>
            </div>
          )}

          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={form.handleSubmit(handleFormSubmit)}
              disabled={isSubmitting}
              size="sm"
              variant="form"
            >
              <span>{isSubmitting ? 'Connecting...' : submitButtonText}</span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  const handleFormSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true)
      await onSubmit({
        clientId: showClientId ? values.clientId : undefined,
        clientSecret: values.clientSecret,
        accountId: showAccountId ? values.accountId : undefined,
      })
      form.reset(values)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        noValidate
        className="space-y-6 max-w-2xl"
      >
        {showAccountId && (
          <FormField
            control={form.control}
            name="accountId"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>{accountIdConfig.label}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={accountIdConfig.placeholder}
                    disabled={isSubmitting}
                    autoFocus
                    {...field}
                  />
                </FormControl>
                {accountIdConfig.description && (
                  <FormDescription>
                    {accountIdConfig.description}
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {showClientId && (
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>{clientIdConfig.label}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={clientIdConfig.placeholder}
                    disabled={isSubmitting}
                    autoFocus={!showAccountId}
                    {...field}
                  />
                </FormControl>
                {clientIdConfig.description && (
                  <FormDescription>
                    {clientIdConfig.description}
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="clientSecret"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>{clientSecretConfig.label}</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={clientSecretConfig.placeholder}
                  disabled={isSubmitting}
                  autoFocus={!showClientId && !showAccountId}
                  {...field}
                />
              </FormControl>
              {clientSecretConfig.description && (
                <FormDescription>
                  {clientSecretConfig.description}
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </form>

      <FloatingIslandContent />
    </Form>
  )
}
